/**
 * "Can this machine afford the expensive thing?" — asked BEFORE we switch an
 * expensive thing on for somebody who never requested it.
 *
 * WHY THIS EXISTS. Krisp noise cancellation defaults itself ON for the teacher's
 * microphone, and the background hook uses this check if a caller supplies an
 * automatic effect default. Both gate only on API PRESENCE —
 * `MediaStreamTrackProcessor` exists, the Krisp module loaded — which answers
 * "is this browser new enough", a question nobody was asking. Neither asks
 * whether the machine can carry the work.
 *
 * On the fleet these classes actually run on that gap matters. Our teachers teach
 * from Windows laptops in the 4 GB Celeron/i3 range, and the failure is not a
 * clean error anyone can act on: segmentation and video encode compete for the
 * same cores, so the teacher's own picture degrades for the whole class while the
 * branded wall renders perfectly. The product looks like it is working. Turning
 * it off is possible and never obvious mid-lesson.
 *
 * WHAT THIS IS NOT. Not a benchmark, and not a gate on the FEATURE — every
 * picker and toggle stays exactly where it was, and anyone who wants blur or
 * noise cancellation on a weak machine gets it, and their stored choice always
 * wins. This only decides what happens to somebody who chose nothing.
 *
 * The signals are all cheap, synchronous and cached, because this is read on the
 * join path.
 */

/**
 * Is WebGL running on the GPU, or on the CPU pretending to be one?
 *
 * THE signal for segmentation, and the reason core count alone is not enough.
 * MediaPipe's selfie segmenter is perfectly affordable on a weak CPU when a real
 * GPU runs the graph — and ruinous when it does not. Windows falls back to
 * software rendering far more readily than anything else we serve: an old Intel
 * integrated GPU whose driver is on Chrome's blocklist, a machine with no
 * working driver at all, or a remote/VM session all end up on SwiftShader or
 * Direct3D's Basic Render Driver, where every frame is composited on the same
 * cores that are trying to encode video.
 *
 * `supportsBackgroundProcessors()` in @livekit/track-processors asks only
 * `getContext('webgl2')` — which SUCCEEDS on SwiftShader. So the library
 * cheerfully reports a machine capable of the very thing that will cripple it,
 * and that is exactly the machine this check exists to find.
 */
function detectGpu(): 'hardware' | 'software' | 'unknown' {
  try {
    const canvas = document.createElement('canvas')
    const gl = (canvas.getContext('webgl2') ??
      canvas.getContext('webgl')) as WebGLRenderingContext | null
    if (!gl) return 'unknown'

    const info = gl.getExtension('WEBGL_debug_renderer_info')
    // The renderer string is what we came for; without the extension (some
    // privacy builds strip it, Brave farbles it) we cannot tell, and 'unknown'
    // must not be read as 'software' — see the callers.
    const renderer = info ? String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL) ?? '') : ''

    // Release it immediately. A held WebGL context costs real memory on the
    // machines this function exists to protect, and browsers cap how many can
    // live at once — leaking one here could starve the processor that follows.
    gl.getExtension('WEBGL_lose_context')?.loseContext()

    if (!renderer) return 'unknown'
    // SwiftShader (Chromium), llvmpipe/softpipe (Mesa), and Basic Render Driver
    // (Windows with no usable GPU driver) are the three that actually show up.
    return /swiftshader|software|llvmpipe|softpipe|basic render|microsoft basic/i.test(renderer)
      ? 'software'
      : 'hardware'
  } catch {
    return 'unknown'
  }
}

/**
 * Logical cores. Absent on older Safari; 0 is never meaningful.
 *
 * Read as a floor, not a score: 2 tells us something real (a dual-core machine
 * encoding video has nothing spare), while 8 does not promise the cores are fast.
 */
function detectCores(): number | undefined {
  // Node (tests) has no navigator — absent, not zero.
  if (typeof navigator === 'undefined') return undefined
  const cores = navigator.hardwareConcurrency
  return typeof cores === 'number' && cores > 0 ? cores : undefined
}

/** Chromium-only, and reported in coarse capped steps (…1, 2, 4, 8). */
function detectMemory(): number | undefined {
  if (typeof navigator === 'undefined') return undefined
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory
  return typeof memory === 'number' && memory > 0 ? memory : undefined
}

export interface DeviceClass {
  gpu: 'hardware' | 'software' | 'unknown'
  cores?: number
  memoryGb?: number
}

let cached: DeviceClass | null = null

/** Probed once per page — none of these change while a lesson runs. */
export function deviceClass(): DeviceClass {
  if (!cached) {
    cached = { gpu: detectGpu(), cores: detectCores(), memoryGb: detectMemory() }
  }
  return cached
}

/** Below this, a machine encoding video has nothing spare for a second pipeline. */
const MIN_CORES = 4
/** Chromium caps this at 8; 2 GB or less is a machine already under pressure. */
const MIN_MEMORY_GB = 4

/**
 * The machine class with nothing spare: software rendering, or cores/memory
 * under the floors. Shared by the questions below because the COST profile
 * is the same — a second heavy pipeline competing with video encode on the
 * same cores. `unknown` signals stay permissive: the extension is missing on
 * privacy-hardened builds and older Safari, and refusing features to everyone
 * whose browser declines to name its renderer would be a much bigger
 * regression than the one being prevented.
 */
function hasSpareCapacity(device: DeviceClass): boolean {
  if (device.gpu === 'software') return false
  if (device.cores !== undefined && device.cores < MIN_CORES) return false
  if (device.memoryGb !== undefined && device.memoryGb < MIN_MEMORY_GB) return false
  return true
}

/**
 * Should per-frame segmentation (blur / virtual background) switch itself on
 * for someone who has not asked for it? Software rendering is disqualifying
 * on its own, whatever the CPU looks like — that is the case where the cost
 * is worst and least visible — and otherwise this wants cores to spare.
 */
export function canAutoEnableSegmentation(): boolean {
  return hasSpareCapacity(deviceClass())
}

/**
 * Should this device publish the full three-layer STUDENT camera ladder?
 * (the TODO(adaptive-02) room-options.ts used to carry.)
 *
 * A heuristic stand-in for the runtime CPU-time signal that TODO really
 * wanted: encode cost tracks the same GPU/cores/memory class that gates
 * segmentation, and the TODO's own surgical fallback was the two-layer
 * ladder. When a real CPU-pressure signal exists, replace this with it.
 */
export function canEncodeFullLadder(device: DeviceClass): boolean {
  return hasSpareCapacity(device)
}

/**
 * Should Krisp noise cancellation switch itself on unasked?
 *
 * Purely a CPU question — it is a WASM audio pipeline with no GPU path at all,
 * running the heaviest NC model we ship, on the one microphone in the room whose
 * audio is the lesson. When it runs short of CPU it does not degrade gracefully;
 * it drops buffers, and a dropped buffer is a glitch every child hears.
 */
export function canAutoEnableNoiseFilter(): boolean {
  return false
}

/** Compact form for telemetry, so field data can second-guess the thresholds above. */
export function deviceClassLabel(): string {
  const { gpu, cores, memoryGb } = deviceClass()
  return `gpu=${gpu},cores=${cores ?? '?'},mem=${memoryGb ?? '?'}`
}
